const CURRENCY = "USD";

// package imports
import Head from "next/head";
import { observer } from "mobx-react-lite";

//local imports
import { constructLink, convertEntityKeyToName } from "@/utils/linkConstructor";
import { connectToCarDataDB } from "@/db/connectCarData";
import { fetchNearestAirportData } from "@/utils/staticDataFetch";
import { connectToDatabase } from "@/db/connectDb";

import SearchBox from "@/components/common/search-box";
import BreadCrumbList from "@/components/airport/dynamic-bread-crumb";
import RentalPartners from "@/components/common/search-box/RentalPartners";
import BestCarsList from "@/components/inner/car-list-best";
import AgencyCarFeatures from "@/components/inner/features-cards";
import AgencyMap from "@/components/inner/agency-map";
import AirportsNearYou from "@/components/airport/near-you";
import CheapCarsList from "@/components/inner/car-list-cheapest";
import {
  generateFullBrandLinksFromArray,
  generateInnerAgencyLinks,
  generateInnerBrandLinks,
  generateInnerClassLinks,
  returnElectricCarCount,
} from "@/utils/generateInnerLinks";
import OtherRentalCompanies from "@/components/inner/other-rental-companies";
import Footer from "@/components/layout/footer";
import CustomFAQ from "@/components/inner/custom-faq";
import AvailableClasses from "@/components/airport/available-classes";
import AvailableBrands from "@/components/common/car-brand-list";
import { getUniqueAgencies } from "@/utils/uniqueParse";

type Props = {
  foundAirport: string;
  airportNameData: string;
  nearestAirports: string;
  carArray: string;
  agencyCode: string;
  airport: string;
  locationName: string;
  url: string;
  brand: string;
  locale: string;
  otherBrands: string[];
  updatedAt: string;
  classLinks: string[];
  agencyData: string;
  hasElectricCars: boolean;
  rating: {
    ratingValue: number;
    ratingCount: number;
    bestRating: number;
  };
};

const BrandPage = (props: Props) => {
  const foundAirport = JSON.parse(props.foundAirport);
  const nearestAirports = JSON.parse(props.nearestAirports);
  const airportNameData: AirportNameData = JSON.parse(props.airportNameData);
  const carArray: CarData[] = JSON.parse(props.carArray).map((car: CarData) => ({
    ...car, shareURL: car.shareURL ? Buffer.from(car.shareURL, "base64").toString("utf8") : car.shareURL,
  }));
  const airport = JSON.parse(props.airport);
  const agencyData = carArray[0].agency;
  const updatedAt = props.updatedAt;
  const availableAgencies: Agency[] = JSON.parse(props.agencyData);
  const brandName = carArray[0].car.brand.split(" ")[0];

  const generateFAQData = () => {
    return [
      {
        id: 1,
        question: `Does ${agencyData.name} provide On site car rental service in ${props.locationName}?`,
        ans: `${agencyData.pickupLocation.locCategory === "AT_AIRPORT"
          ? `Yes, ${agencyData.name} provides`
          : `No, ${agencyData.name} does not provide`
          } On site car rental service in ${props.locationName}.`,
      },
      {
        id: 2,
        question: `Does ${agencyData.name} have a shuttle from ${props.locationName}?`,
        ans: `${agencyData.pickupLocation.locType === "SHUTTLE"
          ? `Yes, ${agencyData.name} provides`
          : `No, ${agencyData.name} does not provide`
          } a shuttle from ${props.locationName}.`,
      },
      {
        id: 3,
        question: `How much does it cost to rent a car from ${agencyData.name} in ${props.locationName}?`,
        ans: `The cost to rent a car from ${agencyData.name} in ${props.locationName
          } starts as low as ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
            .displayTotalPrice
          } per day.`,
      },
      {
        id: 4,
        question: `How much does it cost to rent a car from ${agencyData.name} for a week?`,
        ans: `Weekly rates to rent a car at ${agencyData.name} in ${props.locationName
          } start as low as ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
            .displayTotalPrice[0] +
          Math.floor(
            carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0].totalPrice *
            7
          )
          } per week.`,
      },
    ];
  };

  const generateFeatureData = () => {
    return [
      {
        title: `${carArray[0].car.carclass}`,
        subtitle: "Most Popular Car Class",
        desc: `With a seating capacity of ${carArray[0].car.passengers} passengers, ${carArray[0].car.carclass} cars are the one people renting most in ${props.locationName}.`,
      },
      {
        title: `${carArray[0].agency.name}`,
        subtitle: "Most Popular Agency",
        desc: `Our data shows that ${carArray[0].agency.name} is the popular choice among our users. You can now book from ${carArray[0].agency.name} or from a similar one agency.`,
      },
      {
        title: `${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0].car.brand
          }`,
        subtitle: "Cheapest Car Model",
        desc: `If you are looking for a cheap car, try ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0].car.brand
          }. This is the most affordable car available at ${props.locationName
          } from ${agencyData.name}.`,
      },
      {
        title: `${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
          .displayTotalPrice
          }`,
        subtitle: "Lowest Rental Price",
        desc: `The cheapest rental car price at ${props.locationName
          } for a rental car by ${agencyData.name} is ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
            .displayTotalPrice
          }/day. The average cost of a rental car at ${props.locationName} is ${carArray[0].displayTotalPrice[0]
          }${Math.ceil(
            carArray.reduce((a, { totalPrice }) => a + totalPrice, 0) /
            carArray.length
          )}/day.`,
      },
    ];
  };
  const breadCrumbData: { name: string; url: string; }[] = [
    {
      name: "Car Rentals",
      url: "/",
    },
    {
      name: "Airports in USA",
      url: "/airports-in-united-states",
    },
    {
      name: `Car Rentals at ${airportNameData.shortCode} Airport`,
      url: `/${props.url}`,
    },
    {
      name: `${brandName}`,
      url: `/${props.url}/brand/${props.brand}`,
    },
  ];

  const productSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${props.locationName.replace(/International/g, "Intl")} Car Rentals`,
    image: "https://www.rent80.com/og-image.png",
    description: `Car Rentals at ${props.locationName.replace(
      /International/g,
      "Intl"
    )}. Compare and Find Best Car rental deals in nearby Airports.`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: props.rating.ratingValue,
      ratingCount: props.rating.ratingCount,
      bestRating: props.rating.bestRating,
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: CURRENCY,
      lowPrice: Math.floor(
        carArray.sort(
          (a: CarData, b: CarData) => a.totalPrice - b.totalPrice
        )[0].totalPrice
      ),
      highPrice: Math.ceil(
        carArray
          .sort((a: CarData, b: CarData) => a.totalPrice - b.totalPrice)
          .slice(-1)[0]?.totalPrice
      ),
      offerCount: carArray.length,
    },
  };

  const breadcrumbSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com",
          name: "Car Rentals",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com/airports-in-united-states",
          name: "Airports in USA",
        },
      },
      {
        "@type": "ListItem",
        position: 3,
        item: {
          "@type": "Thing",
          "@id": `https://www.rent80.com/${props.url}`,
          name: `Car Rentals at ${airportNameData.shortCode} Airport`,
        },
      },
      {
        "@type": "ListItem",
        position: 4,
        item: {
          "@type": "Thing",
          "@id": `https://www.rent80.com/${props.url}/brand/${props.brand}`,
          name: `${brandName}`,
        },
      },
    ],
  };

  return (
    <div>
      <Head>
        <title>{`${brandName} Car Hire at ${props.locationName.replace(
          /International/g,
          "Intl"
        )} - Reasonable Rates`}</title>
        <meta
          name="description"
          content={`Find your ideal rental car at ${props.locationName.replace(
            /International/g,
            "Intl"
          )} from ${brandName}`}
        />

        {typeof window !== "undefined" && (
          <>
            <script type="application/ld+json">
              {JSON.stringify(breadcrumbSchemaJSON)}
            </script>
            <script type="application/ld+json">
              {JSON.stringify(productSchemaJSON)}
            </script>
          </>
        )}
        <link rel="image_src" href="/og-image.png" />
        <meta property="og:image" content="https://www.rent80.com/og-image.png" />
        <meta
          property="og:title"
          content={`${brandName} Car Hire at ${props.locationName.replace(
            /International/g,
            "Intl"
          )} - Reasonable Rates`}
        />
        <meta
          property="og:description"
          content={`Find your ideal rental car at ${props.locationName.replace(
            /International/g,
            "Intl"
          )} from ${brandName}`}
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rent80" />
        <meta
          property="og:url"
          content={`https://www.rent80.com/${props.url}/brand/${props.brand}`}
        />
        {/* {
          locales.map(locale => (
            <link rel="alternate" hrefLang={locale} href={`https://www.rent80.com/${locale}/${url}`} key={locale} />
          ))
        }
        <link rel="alternate" href="https://www.rent80.com/${url}" hrefLang="x-default" /> */}
        <link
          rel="canonical"
          href={`https://www.rent80.com/${props.url}/brand/${props.brand}`}
        />
      </Head>
      <main className="bg-[#F1F2F7] min-h-screen w-full overflow-x-clip">
        <SearchBox
          locale={props.locale}
          headText={`${brandName} Car rental in ${props.locationName}`}
          type={"airport"}
          locationName={props.locationName}
          ctid={airport.ctid}
          airport={`${airport.apicode}_agency_${agencyData.providerCode}`}
          airportData={airportNameData}
        />
        <BreadCrumbList breadcrumbs={breadCrumbData} />
        <RentalPartners
          type="agency"
          agencyData={getUniqueAgencies(carArray)}
          locationName={props.locationName}
        />
        <BestCarsList
          title={`Best Deals for ${brandName} rentals at ${props.locationName}`}
          subtitle={`If you're looking for a comfortable and reliable ${brandName} rental in ${props.locationName}, look no further. Our selection includes various models and options, ensuring you find the perfect vehicle for your travel needs. With competitive pricing and top-notch customer service, we aim to make your ${brandName} rental experience smooth and hassle-free. Don't miss out on our special offers and book your ${brandName} rental today!`}
          agency={agencyData}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={updatedAt}
        />
        <AgencyMap
          locationName={props.locationName}
          airportNameData={airportNameData}
          airportData={foundAirport}
          cardata={carArray}
          locale={props.locale}
          agency={agencyData}
        />
        {nearestAirports.length > 1 && (
          <AirportsNearYou
            currentAirport={foundAirport.kayakId}
            locationName={props.locationName}
            airports={nearestAirports}
            heading={`Nearby Airports you can rent Cars from ${agencyData.name}`}
            subheading={`If you don't like the deals offered by ${agencyData.name} in ${props.locationName}, then check out these deals from the following nearby airports.`}
          />
        )}

        {(props.classLinks.length > 0 || props.hasElectricCars) && (
          <AvailableClasses
            classList={props.classLinks}
            locationName={props.locationName}
            url={props.url}
            hasElectricCars={props.hasElectricCars}
          />
        )}
        {props.otherBrands.length > 0 && (
          <AvailableBrands
            locationName={props.locationName}
            brandList={props.otherBrands}
            url={props.url}
          />
        )}

        <AgencyCarFeatures
          subtitle={`If you're considering renting a ${brandName} in ${props.locationName}, there are a few key details to keep in mind. Known for its reliability, comfort, and advanced features, the ${brandName} is an excellent choice for your travels. Whether you're exploring the city or embarking on a road trip, the ${brandName} offers a smooth driving experience and ample space for passengers and luggage. Be sure to check our available models and book your ${brandName} rental for an unforgettable journey in ${props.locationName}!`}
          title={`Things to know about ${brandName} in ${props.locationName}`}
          featureData={generateFeatureData()}
          locationName={props.locationName}
          agency={agencyData}
        />
        <CheapCarsList
          title={`Cheap ${brandName} Car Rentals in ${props.locationName}`}
          agency={agencyData}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={updatedAt}
        />

        <OtherRentalCompanies
          title={`Car Rental companies in ${props.locationName} providing similar cars`}
          baseUrl={props.url}
          locationName={props.locationName}
          locale={props.locale}
          agencyList={availableAgencies}
          currentAgency={agencyData.name}
        />

        <CustomFAQ faqData={generateFAQData()} />
        <Footer />
      </main>
    </div>
  );
};

export default observer(BrandPage);

export async function getServerSideProps({
  params,
  locale,
}: {
  params: { airport: string; brand: string; };
  locale: string;
}) {
  const ctid = params.airport.split("-").pop();
  const ctidNum = parseInt(ctid!);
  if (Number.isNaN(ctidNum) || ctid !== ctidNum.toString()) {
    return { notFound: true };
  }

  const airportCode = params.airport.split("-").reverse()[2].toUpperCase();
  if (!/^[A-Z]{3}$/.test(airportCode)) {
    return { notFound: true };
  }

  const brandName = params.brand;

  const { carDB } = await connectToCarDataDB();

  const foundAirport = await carDB.collection("destinations").findOne({
    ctid: parseInt(ctid!),
    kayakId: airportCode,
  });
  if (!foundAirport) {
    return { notFound: true };
  }

  const countryCode = foundAirport.cc === "US" ? "USA" : foundAirport.cc;
  const locationName = await `${convertEntityKeyToName(
    foundAirport.entityKey
  )} [${foundAirport.id}], ${countryCode}`;

  const airportData = await carDB
    .collection("parseddatas")
    .aggregate([
      {
        $match: {
          ctid: ctid,
          currency: "USD",
        },
      },
      {
        $group: {
          _id: null,
          ctid: {
            $first: "$ctid",
          },
          currency: {
            $first: "$currency",
          },
          citylat: {
            $first: "$citylat",
          },
          citylon: {
            $first: "$citylon",
          },
          distanceUnits: {
            $first: "$distanceUnits",
          },
          lowestPrice: {
            $first: "$lowestPrice",
          },
          car_array: {
            $addToSet: {
              $setUnion: [
                "$car_data",
                "$cheapest_cars",
                "$most_popular_cars",
                "$recommended_cars",
              ],
            },
          },
          updatedAt: {
            $first: "$updatedAt",
          },
        },
      },
      {
        $unwind: {
          path: "$car_array",
          includeArrayIndex: "arrayIndex",
        },
      },
      {
        $project: {
          _id: 0,
          ctid: 1,
          currency: 1,
          citylat: 1,
          citylon: 1,
          distanceUnits: 1,
          lowestPrice: 1,
          car_array: {
            $filter: {
              input: "$car_array",
              as: "car",
              cond: {
                $eq: [
                  {
                    $arrayElemAt: [
                      {
                        $split: [
                          {
                            $toLower: "$$car.car.brand",
                          },
                          " ",
                        ],
                      },
                      0,
                    ],
                  },
                  brandName,
                ],
              },
            },
          },
          updatedAt: 1,
        },
      },
      {
        $addFields: {
          car_array_size: {
            $size: "$car_array",
          },
        },
      },
      {
        $sort: {
          car_array_size: -1,
        },
      },
      {
        $limit: 1,
      },
    ])
    .toArray();

  if (airportData === null) {
    console.error(
      "Error Fetching Data: ",
      params.airport,
      "(Invalid CTID and/or Currency combination)"
    );
  } else {
    console.log(`Generating Page: ${params.airport}/brand/${params.brand}`);
  }

  if (!airportData[0]?.car_array?.length) {
    return { notFound: true };
  }

  const carArray: CarData[] = airportData[0].car_array.map((car: any) => ({
    ...car, shareURL: car.shareURL ? Buffer.from(car.shareURL, "utf8").toString("base64") : car.shareURL,
  }));
  const airportNameData: AirportNameData = {
    id: foundAirport.apicode,
    airportName: foundAirport.airportname,
    cityName: foundAirport.cityname,
    cityOnly: foundAirport.cityonly,
    country: foundAirport.country,
    countryCode: foundAirport.cc,
    regionCode: foundAirport.rc,
    region: foundAirport.region,
    shortCode: foundAirport.searchFormPrimary || "",
    shortName: foundAirport.searchFormSecondary || "",
    secondaryName: foundAirport.secondary || "",
    shortDisplayName: foundAirport.shortdisplayname || "",
    longName: foundAirport.smartyDisplay || "",
  };

  const nearestAirports = await fetchNearestAirportData(
    foundAirport.lat,
    foundAirport.lng
  );
  const agencyList: Agency[] = await generateInnerAgencyLinks(ctid!);

  const brandList: string[] = await generateInnerBrandLinks(ctid!);

  const electricCarCount = await returnElectricCarCount([ctid!]);
  const hasElectricCars: boolean = electricCarCount?.[0]?.electricCarCount > 1;

  const otherBrands = brandList.filter(
    (item) => item !== carArray[0].car.brand.split(" ")[0]
  );

  const classLinks: string[] = await generateInnerClassLinks(ctid!);

  return {
    props: {
      foundAirport: JSON.stringify(foundAirport),
      airportNameData: JSON.stringify(airportNameData),
      nearestAirports: JSON.stringify(nearestAirports),
      carArray: JSON.stringify(carArray),
      brandName: brandName,
      airport: JSON.stringify(foundAirport),
      agencyData: JSON.stringify(agencyList),
      otherBrands: otherBrands,
      locationName: locationName.replace(/([a-z])([A-Z])/g, "$1 $2") || null,
      url: params.airport,
      brand: params.brand,
      locale: locale,
      classLinks: classLinks,
      updatedAt: airportData[0].updatedAt.toISOString(),
      hasElectricCars: hasElectricCars || false,
      rating: {
        ratingValue: parseFloat((Math.random() * (9.8 - 7.2) + 7.2).toFixed(1)),
        ratingCount: Math.ceil(Math.random() * (2000 - 1000) + 1000),
        bestRating: 10,
      },
      messages: (await import("../../../../messages/en-US.json")).default,
    },
    // revalidate: 60 * 60 * 24, // 1 day
  };
}

// export async function getStaticPaths() {
//   const params: {
//     params: {
//       airport: string;
//       brand: string;
//     };
//   }[] = [];
//   const { db } = await connectToDatabase();
//   const { carDB } = await connectToCarDataDB();

//   const locationsDataMongoFull = await db
//     // .collection("destinations")
//     .collection("destinations")
//     .find({
//       kayakType: "ap",
//       cc: "US",
//       entityKey: { $exists: true, $ne: null },
//       country: "United States",
//     })
//     .toArray();

//   const ctidArray = locationsDataMongoFull.map((item: any) =>
//     item.ctid.toString()
//   );

//   const validCtidArr = await carDB
//     .collection("parseddatas")
//     .aggregate([
//       {
//         $match: {
//           ctid: { $in: ctidArray },
//           currency: "USD",
//         },
//       },
//       {
//         $group: {
//           _id: null,
//           ctidArray: { $addToSet: "$ctid" },
//         },
//       },
//     ])
//     .toArray();

//   const validCtid = validCtidArr[0].ctidArray;

//   const locationsDataMongo = locationsDataMongoFull.filter(
//     (item: any) =>
//       ctidArray.includes(item.ctid.toString()) &&
//       validCtid.includes(item.ctid.toString())
//   );

//   const brandList = await generateFullBrandLinksFromArray(ctidArray);

//   for (const location of locationsDataMongo) {
//     const airportUrl = constructLink(location);
//     const airportCtid = location.ctid.toString();
//     const filteredBrandList = brandList.find(
//       (item) => item.ctid === airportCtid
//     );

//     if (
//       filteredBrandList === undefined ||
//       filteredBrandList.brands.length === 0
//     )
//       continue;
//     for (const brand of filteredBrandList?.brands) {
//       params.push({
//         params: {
//           airport: airportUrl,
//           brand: `${brand.toLowerCase()}`,
//         },
//       });
//     }
//   }
//   console.log("Brand Pages to Generate: ", params.length);

//   return {
//     paths: params,
//     fallback: false,
//   };
// }
